import type { JobSalary } from "@lossdog/job-watchlists";
import { Typography } from "@povio/ui";
import type { ColumnDef, Header, Table } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import CustomTooltipTrigger from "@/components/ui/overlays/Tooltip/CustomTooltipTrigger";
import { TooltipEllipsis } from "@/components/ui/overlays/Tooltip/TooltipEllipsis";
import TableCell from "@/components/ui/table/TableCell";
import { VirtualizedTableWithStickyHeader } from "@/components/ui/table/VirtualizedTableWithStickyHeader";
import WatchlistTableHeaderCell from "@/components/watchlist/components/WatchlistTableHeaderCell";
import { JobSalaryLastPrice } from "@/components/watchlist/job-salary-ticker/JobSalaryLastPrice";
import { JobSalaryNetChange } from "@/components/watchlist/job-salary-ticker/JobSalaryNetChange";

interface SimilarJobPositionsProps {
  relatedJobSalaries: JobSalary[];
  onSelectedJobSalary: (jobSalary: JobSalary) => void;
}

const createColumns = (t: TFunction): ColumnDef<JobSalary>[] => [
  {
    accessorKey: "title",
    size: 300,
    header: ({ header, table }) => SearchSectionHeaderCell(header, t("watchlist.filters.position"), table),
    enableSorting: false,
    cell: (info) => SearchSectionCell(info),
  },
  {
    accessorKey: "salary",
    header: ({ header, table }) => SearchSectionHeaderCell(header, t("jobDetails.similarJobs.salary"), table),
    size: 80,
    maxSize: 80,
    enableSorting: false,
    minSize: 80,
    cell: (info) => SearchSectionCellSalary(info),
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.salary.value || 0;
      const b = rowB.original.salary.value || 0;
      return a - b;
    },
  },
  {
    accessorKey: "netChange",
    header: ({ header, table }) => SearchSectionHeaderCell(header, t("jobDetails.similarJobs.change"), table, "center"),
    enableSorting: false,
    size: 60,
    cell: (info) => SearchSectionCellNetChange(info),
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.netChange.value || 0;
      const b = rowB.original.netChange.value || 0;
      return a - b;
    },
  },
];

export const SimilarJobPositionsTable = ({ relatedJobSalaries, onSelectedJobSalary }: SimilarJobPositionsProps) => {
  const { t } = useTranslation();

  const columns = createColumns(t);

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex h-full flex-1 flex-col gap-1-5 overflow-hidden">
      <Typography
        size="title-3"
        variant="prominent-1"
        className="px-4 py-1-5 text-text-default-1"
      >
        {t("jobDetails.similarJobs.title")}
      </Typography>

      <div
        className="h-full min-h-[220px] overflow-y-auto"
        ref={containerRef}
      >
        <VirtualizedTableWithStickyHeader
          data={relatedJobSalaries}
          columns={columns}
          sorting={[]}
          onSortingChange={() => false}
          onRowDataUsed={(rowData) => rowData.subscribe()}
          containerClassName="h-full min-h-[300px]"
          onRowClick={(rowData) => {
            onSelectedJobSalary(rowData);
          }}
        />
      </div>
    </div>
  );
};

const SearchSectionHeaderCell = (
  header: Header<any, unknown>,
  label: string,
  table: Table<JobSalary>,
  align: "start" | "center" | "end" = "start",
) => {
  return (
    <WatchlistTableHeaderCell
      header={header}
      label={label}
      align={align}
      table={table}
    />
  );
};

const SearchSectionCell = (info: any) => {
  return (
    <TableCell align="start">
      <TooltipEllipsis
        text={info.row.original.title}
        offset={30}
        placement="top left"
      >
        {(onContentRef) => (
          <CustomTooltipTrigger className="relative h-full w-full">
            <Typography
              ref={onContentRef}
              size="body-4"
              className="w-96 truncate"
            >
              {info.row.original.title}
            </Typography>
          </CustomTooltipTrigger>
        )}
      </TooltipEllipsis>
    </TableCell>
  );
};

const SearchSectionCellSalary = (info: any) => {
  return (
    <TableCell align="start">
      <Typography
        size="body-4"
        variant="prominent-1"
      >
        <JobSalaryLastPrice jobSalary={info.row.original} />
      </Typography>
    </TableCell>
  );
};

const SearchSectionCellNetChange = (info: any) => {
  return (
    <TableCell
      className="flex flex-row items-center gap-2"
      align="center"
    >
      <Typography
        size="body-3"
        variant="prominent-1"
      >
        <JobSalaryNetChange jobSalary={info.row.original} />
      </Typography>
    </TableCell>
  );
};
